/**
 * A document's own section, as rules: its heading, the line naming the
 * issuer, the summary, the key-facts table, the "Note:" lines its
 * caveats become, and which key it is rendered under. Pure, so the screen
 * and the tests say the same. Under schema version 2 only a document
 * that is neither corporate nor financial (a certificate of good
 * standing, articles of association) has a section; under version 3
 * every read document does.
 */

import {
  documentPartOfFilename,
  REGISTRY_DIGEST_SCHEMA_VERSION,
  type RegistryDigestDocumentSection,
  type RegistryDocumentDigest,
} from '@/types/registry-documents';
import { DEFAULT_REGISTRY_NAME, NOT_INDICATED, type LabelValueRow } from './digestTemplate';

export const DOCUMENT_WORDS = {
  /** The title of a section the API sent none for, and that names no
   * document and no file either. */
  untitled: 'Document',
  noInformation: 'No information about the company was identified in the document reviewed.',
} as const;

/** The section's heading: the title the API resolved (the catalogue's
 * name for the product), else the document's own title, else the
 * filename's document part. */
export function documentSectionTitle(
  section: Pick<RegistryDigestDocumentSection, 'title' | 'documentTitle' | 'filename'>
): string {
  return (
    section.title.trim() ||
    section.documentTitle?.trim() ||
    documentPartOfFilename(section.filename) ||
    DOCUMENT_WORDS.untitled
  );
}

/** The line under the heading, as the Word file words it: the document's
 * title as printed (else the heading), when it was issued and by whom:
 * "Certificate of Good Standing, issued 2 October 2026 by the Registrar of
 * Companies." Null when the document states none of the three. */
export function documentIssueLine(
  section: Pick<
    RegistryDigestDocumentSection,
    'title' | 'documentTitle' | 'filename' | 'issued' | 'issuer'
  >
): string | null {
  const printed = section.documentTitle?.trim();
  const issued = section.issued?.trim();
  const issuer = section.issuer?.trim();
  if (!printed && !issued && !issuer) {
    return null;
  }
  let line = printed || documentSectionTitle(section);
  if (issued) {
    line += `, issued ${issued}`;
  }
  if (issuer) {
    line += issued ? ` by ${issuer}` : `, issued by ${issuer}`;
  }
  // An issuer that ends in an abbreviation ("Ltd.") ends the sentence.
  return line.endsWith('.') ? line : `${line}.`;
}

/** The key-facts table's rows, in the document's order; a fact without a
 * value reads "not indicated", one without a label is left out. */
export function keyFactRows(
  section: Pick<RegistryDigestDocumentSection, 'keyFacts'>
): LabelValueRow[] {
  return section.keyFacts
    .filter((fact) => fact.label.trim())
    .map((fact) => {
      const value = fact.value.trim();
      return {
        label: fact.label.trim(),
        value: value || NOT_INDICATED,
        provided: Boolean(value),
      };
    });
}

/** Whether the section has anything to show beyond its heading. */
export function documentHasContent(
  section: Pick<RegistryDigestDocumentSection, 'keyFacts' | 'summary'>
): boolean {
  return keyFactRows(section).length > 0 || Boolean(section.summary?.trim());
}

/** Whether the digest keeps an entry per read document (schema version
 * 3), the order-wide tables derived from them: the screen then lays the
 * entries out and folds the derived tables away. */
export function isPerDocumentDigest(
  digest: Pick<RegistryDocumentDigest, 'schemaVersion'>
): boolean {
  return digest.schemaVersion >= REGISTRY_DIGEST_SCHEMA_VERSION;
}

/** The key an entry is rendered under: the file's hash, else its place
 * in the list (a version 2 entry carries no hash). */
export function documentEntryKey(
  section: Pick<RegistryDigestDocumentSection, 'sha256'>,
  index: number
): string {
  return section.sha256 ?? `document:${index}`;
}

type EntryHead = Pick<
  RegistryDigestDocumentSection,
  'title' | 'documentTitle' | 'filename' | 'issued' | 'issuer' | 'summary' | 'head'
>;

/** The document's title as the report heads it: the head's, else the
 * section's as printed, else the section title. */
function entryTitle(section: EntryHead): string {
  return (
    section.head?.documentTitle?.trim() ||
    section.documentTitle?.trim() ||
    documentSectionTitle(section)
  );
}

/** The heading of a document's section, as the report words it:
 * "{document title} ({registry}, {issued})", the parenthesis holding
 * whichever of the two the document gives. The registry is the head's,
 * else the order's when a source names one; the placeholder registry
 * name is never written into a heading. */
export function documentEntryHeading(section: EntryHead, registry: string | null): string {
  const title = entryTitle(section);
  const named =
    section.head?.registry?.trim() ||
    (registry && registry !== DEFAULT_REGISTRY_NAME ? registry : null);
  const issued = section.head?.issued?.trim() || section.issued?.trim() || null;
  const qualifiers = [named, issued].filter((part): part is string => Boolean(part));
  return qualifiers.length > 0 ? `${title} (${qualifiers.join(', ')})` : title;
}

/** "Issued by {issuer}." under the heading; null when the document names
 * none. An issuer that ends in an abbreviation ("Ltd.") ends the line. */
export function documentIssuerLine(section: Pick<EntryHead, 'issuer' | 'head'>): string | null {
  const issuer = section.head?.issuer?.trim() || section.issuer?.trim();
  if (!issuer) {
    return null;
  }
  const line = `Issued by ${issuer}`;
  return line.endsWith('.') ? line : `${line}.`;
}

/** What the document is and what it tells the reader: the head's
 * summary, else the section's. Null when neither says. */
export function documentEntrySummary(section: Pick<EntryHead, 'summary' | 'head'>): string | null {
  return section.head?.summary?.trim() || section.summary?.trim() || null;
}

/** The document's caveats as the report carries them: one short "Note:"
 * line each, blank ones left out. */
export function documentNoteLines(
  section: Pick<RegistryDigestDocumentSection, 'caveats'>
): string[] {
  return section.caveats
    .map((caveat) => caveat.trim())
    .filter(Boolean)
    .map((caveat) => `Note: ${caveat}`);
}
